import React, { useState, useEffect, useCallback } from 'react';

import {
  SafetyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Card,
  Button,
  Space,
  Tag,
  Steps,
  Input,
  Alert,
  Modal,
  Typography,
  Spin,
  message,
  Descriptions,
} from 'antd';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';

import authService from '@services/admin/authService';

const { Text, Paragraph } = Typography;

const TotpSettings = ({ onEnabled }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [totpStatus, setTotpStatus] = useState(null);

  // Setup flow
  const [setupMode, setSetupMode] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);

  // Backup codes display
  const [backupCodes, setBackupCodes] = useState(null);

  // Disable modal
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);

  // Regenerate modal
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [regeneratePassword, setRegeneratePassword] = useState('');
  const [regenerateLoading, setRegenerateLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const result = await authService.totpStatus();
      if (result.success) {
        setTotpStatus(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch TOTP status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleStartSetup = async () => {
    try {
      setSetupLoading(true);
      const result = await authService.totpSetup();
      if (result.success && result.data) {
        setSetupData(result.data);
        const dataUrl = await QRCode.toDataURL(result.data.provisioning_uri, {
          width: 200,
          margin: 2,
        });
        setQrDataUrl(dataUrl);
        setSetupMode(true);
      } else {
        message.error(t('admin.totp.settings.setup_failed'));
      }
    } catch (err) {
      message.error(t('admin.totp.settings.setup_failed'));
    } finally {
      setSetupLoading(false);
    }
  };

  const handleConfirmEnable = async () => {
    if (!confirmCode || confirmCode.length !== 6) return;

    try {
      setSetupLoading(true);
      const result = await authService.totpEnable(confirmCode);
      if (result.success && result.data) {
        message.success(t('admin.totp.settings.enable_success'));
        setBackupCodes(result.data.backup_codes);
        setSetupMode(false);
        setSetupData(null);
        setQrDataUrl(null);
        setConfirmCode('');
        fetchStatus();
        onEnabled?.();
      } else {
        message.error(t('admin.totp.settings.enable_failed'));
      }
    } catch (err) {
      message.error(t('admin.totp.settings.enable_failed'));
    } finally {
      setSetupLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) return;

    try {
      setDisableLoading(true);
      const result = await authService.totpDisable(disablePassword);
      if (result.success) {
        message.success(t('admin.totp.settings.disable_success'));
        setDisableModalOpen(false);
        setDisablePassword('');
        setBackupCodes(null);
        fetchStatus();
      } else {
        message.error(t('admin.totp.settings.disable_failed'));
      }
    } catch (err) {
      message.error(t('admin.totp.settings.disable_failed'));
    } finally {
      setDisableLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!regeneratePassword) return;

    try {
      setRegenerateLoading(true);
      const result = await authService.totpRegenerateBackupCodes(regeneratePassword);
      if (result.success && result.data) {
        message.success(t('admin.totp.settings.regenerate_success'));
        setBackupCodes(result.data.backup_codes);
        setRegenerateModalOpen(false);
        setRegeneratePassword('');
        fetchStatus();
      } else {
        message.error(t('admin.totp.settings.regenerate_failed'));
      }
    } catch (err) {
      message.error(t('admin.totp.settings.regenerate_failed'));
    } finally {
      setRegenerateLoading(false);
    }
  };

  const handleCopyCodes = () => {
    if (backupCodes) {
      navigator.clipboard.writeText(backupCodes.join('\n'));
      message.success(t('admin.totp.settings.codes_copied'));
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin />
      </div>
    );
  }

  const isEnabled = totpStatus?.totp_enabled;
  const isRequiredByOrg = totpStatus?.totp_required_by_organization;

  return (
    <>
      {/* Organization 強制要求提示 */}
      {isRequiredByOrg && !isEnabled && (
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message={t('admin.totp.settings.org_required_title')}
          description={t('admin.totp.settings.org_required_desc')}
          style={{ marginBottom: 16 }}
        />
      )}

      <Paragraph type="secondary">
        {t('admin.totp.settings.description')}
      </Paragraph>

      {isEnabled ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 14, padding: '4px 12px' }}>
            {t('admin.totp.settings.status_enabled')}
          </Tag>

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('admin.totp.settings.enabled_at')}>
              {totpStatus?.totp_enabled_at
                ? new Date(totpStatus.totp_enabled_at).toLocaleString()
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.totp.settings.backup_codes_title')}>
              {t('admin.totp.settings.backup_codes_remaining', {
                count: totpStatus?.backup_codes_remaining || 0,
              })}
            </Descriptions.Item>
          </Descriptions>

          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => setRegenerateModalOpen(true)}
            >
              {t('admin.totp.settings.regenerate_backup_codes')}
            </Button>
            <Button
              danger
              onClick={() => setDisableModalOpen(true)}
            >
              {t('admin.totp.settings.disable_button')}
            </Button>
          </Space>
        </Space>
      ) : setupMode ? (
        <Steps
          direction="vertical"
          current={-1}
          items={[
            {
              title: t('admin.totp.settings.setup_step1'),
              description: t('admin.totp.settings.setup_step1_desc'),
              status: 'finish',
            },
            {
              title: t('admin.totp.settings.setup_step2'),
              description: (
                <Space direction="vertical" size="middle" style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    {t('admin.totp.settings.setup_step2_desc')}
                  </Text>
                  {qrDataUrl && (
                    <div style={{
                      background: 'white',
                      padding: 16,
                      borderRadius: 8,
                      border: '1px solid #f0f0f0',
                      display: 'inline-block',
                    }}>
                      <img src={qrDataUrl} alt="TOTP QR Code" />
                    </div>
                  )}
                  {setupData?.secret && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('admin.totp.settings.manual_key')}:
                      </Text>
                      <br />
                      <Text code copyable style={{ fontSize: 14 }}>
                        {setupData.secret}
                      </Text>
                    </div>
                  )}
                </Space>
              ),
              status: 'process',
            },
            {
              title: t('admin.totp.settings.setup_step3'),
              description: (
                <Space direction="vertical" size="small" style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    {t('admin.totp.settings.setup_step3_desc')}
                  </Text>
                  <Space>
                    <Input
                      placeholder={t('admin.totp.settings.confirm_code_placeholder')}
                      maxLength={6}
                      style={{ width: 200 }}
                      value={confirmCode}
                      onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ''))}
                      onPressEnter={handleConfirmEnable}
                    />
                    <Button
                      type="primary"
                      loading={setupLoading}
                      disabled={confirmCode.length !== 6}
                      onClick={handleConfirmEnable}
                    >
                      {t('admin.totp.settings.confirm_enable')}
                    </Button>
                    <Button onClick={() => {
                      setSetupMode(false);
                      setSetupData(null);
                      setQrDataUrl(null);
                      setConfirmCode('');
                    }}>
                      {t('admin.totp.settings.cancel')}
                    </Button>
                  </Space>
                </Space>
              ),
              status: 'wait',
            },
          ]}
        />
      ) : (
        <Button
          type="primary"
          icon={<SafetyOutlined />}
          loading={setupLoading}
          onClick={handleStartSetup}
        >
          {t('admin.totp.settings.enable_button')}
        </Button>
      )}

      {/* Backup Codes Display */}
      {backupCodes && (
        <Card
          title={t('admin.totp.settings.backup_codes_title')}
          style={{ marginTop: 16 }}
          size="small"
          extra={
            <Button
              icon={<CopyOutlined />}
              size="small"
              onClick={handleCopyCodes}
            >
              {t('admin.totp.settings.copy_codes')}
            </Button>
          }
        >
          <Alert
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            message={t('admin.totp.settings.backup_codes_warning')}
            style={{ marginBottom: 16 }}
          />
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px',
            maxWidth: 400,
          }}>
            {backupCodes.map((code, i) => (
              <Text key={i} code style={{ fontSize: 14, textAlign: 'center', padding: '4px 8px' }}>
                {code}
              </Text>
            ))}
          </div>
        </Card>
      )}

      {/* Disable TOTP Modal */}
      <Modal
        title={t('admin.totp.settings.disable_confirm_title')}
        open={disableModalOpen}
        onCancel={() => {
          setDisableModalOpen(false);
          setDisablePassword('');
        }}
        onOk={handleDisable}
        okText={t('admin.totp.settings.confirm_disable')}
        okButtonProps={{ danger: true, loading: disableLoading, disabled: !disablePassword }}
        cancelText={t('admin.totp.settings.cancel')}
      >
        <Paragraph type="secondary">
          {t('admin.totp.settings.disable_confirm_desc')}
        </Paragraph>
        <Input.Password
          placeholder={t('admin.totp.settings.password_placeholder')}
          value={disablePassword}
          onChange={(e) => setDisablePassword(e.target.value)}
          onPressEnter={handleDisable}
        />
      </Modal>

      {/* Regenerate Backup Codes Modal */}
      <Modal
        title={t('admin.totp.settings.regenerate_confirm_title')}
        open={regenerateModalOpen}
        onCancel={() => {
          setRegenerateModalOpen(false);
          setRegeneratePassword('');
        }}
        onOk={handleRegenerate}
        okText={t('admin.totp.settings.regenerate_backup_codes')}
        okButtonProps={{ loading: regenerateLoading, disabled: !regeneratePassword }}
        cancelText={t('admin.totp.settings.cancel')}
      >
        <Paragraph type="secondary">
          {t('admin.totp.settings.regenerate_confirm_desc')}
        </Paragraph>
        <Input.Password
          placeholder={t('admin.totp.settings.password_placeholder')}
          value={regeneratePassword}
          onChange={(e) => setRegeneratePassword(e.target.value)}
          onPressEnter={handleRegenerate}
        />
      </Modal>
    </>
  );
};

export default TotpSettings;
